import emailjs from '@emailjs/browser';

// EmailJS credentials — set these in your .env file:
//   REACT_APP_EMAILJS_SERVICE_ID=service_xxxxxxx
//   REACT_APP_EMAILJS_PUBLIC_KEY=xxxxxxxxxxxxxxx
//   REACT_APP_EMAILJS_TEMPLATE_APPROVAL=template_xxxxxxx
//   REACT_APP_EMAILJS_TEMPLATE_ORDER=template_xxxxxxx
//   REACT_APP_EMAILJS_TEMPLATE_STATUS=template_xxxxxxx
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const APPROVAL_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_APPROVAL || '';
const ORDER_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ORDER || '';
const STATUS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_STATUS || '';
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

if (PUBLIC_KEY) {
  emailjs.init(PUBLIC_KEY);
}

const isConfigured = (): boolean => {
  return !!(SERVICE_ID && PUBLIC_KEY);
};

interface ApprovalEmailParams {
  to_email: string;
  to_name: string;
  business_name: string;
  temp_password: string;
}

interface OrderConfirmationParams {
  to_email: string;
  to_name: string;
  order_number: string;
  order_total: string;
  items_summary: string;
}

interface OrderStatusUpdateParams {
  to_email: string;
  to_name: string;
  order_number: string;
  new_status: string;
  tracking_number?: string;
}

export const sendApprovalEmail = async (params: ApprovalEmailParams): Promise<void> => {
  if (!isConfigured()) {
    console.warn('EmailJS not configured — skipping approval email');
    return;
  }
  try {
    await emailjs.send(SERVICE_ID, APPROVAL_TEMPLATE_ID, {
      to_email: params.to_email,
      to_name: params.to_name,
      business_name: params.business_name,
      temp_password: params.temp_password,
      login_url: window.location.origin,
      support_email: 'info@theyaadshop.com'
    });
  } catch (error) {
    console.error('Failed to send approval email:', error);
    throw new Error('Failed to send approval email. Please try again.');
  }
};

export const sendOrderConfirmation = async (params: OrderConfirmationParams): Promise<void> => {
  if (!isConfigured()) {
    console.warn('EmailJS not configured — skipping order confirmation email');
    return;
  }
  try {
    await emailjs.send(SERVICE_ID, ORDER_TEMPLATE_ID, {
      to_email: params.to_email,
      to_name: params.to_name,
      order_number: params.order_number,
      order_total: params.order_total,
      items_summary: params.items_summary,
      payment_email: 'info@theyaadshop.com',
      support_email: 'info@theyaadshop.com'
    });
  } catch (error) {
    console.error('Failed to send order confirmation email:', error);
    throw new Error('Failed to send order confirmation email.');
  }
};

export const sendOrderStatusUpdate = async (params: OrderStatusUpdateParams): Promise<void> => {
  if (!isConfigured()) {
    console.warn('EmailJS not configured — skipping status update email');
    return;
  }

  const statusMessages: Record<string, string> = {
    'payment_received': 'We have received your payment and your order is being processed.',
    'processing': 'Your order is being prepared for shipment.',
    'shipped': `Your order has been shipped!${params.tracking_number ? ` Tracking number: ${params.tracking_number}` : ''}`,
    'delivered': 'Your order has been delivered. Thank you for your business!'
  };

  try {
    await emailjs.send(SERVICE_ID, STATUS_TEMPLATE_ID, {
      to_email: params.to_email,
      to_name: params.to_name,
      order_number: params.order_number,
      status_message: statusMessages[params.new_status] || `Order status updated to: ${params.new_status}`,
      tracking_number: params.tracking_number || '',
      support_email: 'info@theyaadshop.com'
    });
  } catch (error) {
    console.error('Failed to send status update email:', error);
  }
};
